import "./SupportSearch.css";
import { useState, useRef, useEffect, useMemo } from "react";
import { useLearnCtx } from "./LearnPage";
import { useHint } from "../../hooks/useHint";
import HintBubble from "./HintBubble";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { updateProgressConceptsUsed } from "../../actions/progress";

/**
 * Compact search box shown in the right rail when neither CommentsPanel
 * nor SupportPanel is open. Filters support.json titles and keywords.
 */
export default function SupportSearch() {
  const { openSupport } = useLearnCtx();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dispatch = useDispatch();
  const { id: courseId } = useParams();
  const conceptsUsed = useSelector(
    (state) => state.ProgressReducer.conceptsUsed,
  );
  const { visible: hintVisible, dismiss: dismissHint } = useHint(
    "support-search",
    {
      value: conceptsUsed,
      onDismiss: () => dispatch(updateProgressConceptsUsed(courseId)),
    },
  );

  // Retrieve data from reducer
  const rawSupport = useSelector((state) => state.SupportReducer.supportItems);

  /* Build a flat, alphabetically-sorted list of all titles + keywords.
   Records without a keywords array are figure/equation refs from the main
  text — they are opened directly via canvas links, not via search. */
  const searchItems = useMemo(
    () =>
      rawSupport
        ?.filter((record) => Array.isArray(record.keywords) && record.title)
        .flatMap((record) => {
          const titleLower = record.title.toLowerCase();
          return [
            {
              id: record._id,
              recordTitle: record.title,
              label: record.title,
              type: "title",
            },
            ...record.keywords
              .filter((kw) => kw.toLowerCase() !== titleLower)
              .map((kw) => ({
                id: record._id,
                recordTitle: record.title,
                label: kw,
                type: "keyword",
              })),
          ];
        })
        .filter((item) => item.label)
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        ),
    [rawSupport],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return searchItems;
    return searchItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [query, searchItems]);

  /* Reset highlight when filter changes */
  useEffect(() => {
    setHighlightIdx(0);
  }, [filtered]);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  const handleSelect = (item) => {
    dismissHint();
    openSupport(item.id);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlightIdx]) handleSelect(filtered[highlightIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  /* Scroll highlighted item into view */
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlightIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  return (
    <div className="ss" ref={containerRef}>
      <div style={{ position: "relative" }}>
        <div className="ss__input-row">
          <SearchIcon />
          <input
            ref={inputRef}
            className="ss__input"
            type="text"
            placeholder="Search support topics…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              dismissHint();
              setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            aria-label="Search support topics"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-haspopup="listbox"
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              className="ss__clear"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
              tabIndex={-1}
            >
              ✕
            </button>
          )}
        </div>
        {hintVisible && (
          <HintBubble
            text="Use this box to look up background information about the ideas in the text"
            onDismiss={dismissHint}
            anchor="below"
          />
        )}
      </div>

      {open && (
        <ul
          className="ss__dropdown"
          ref={listRef}
          role="listbox"
          aria-label="Support topics"
        >
          {filtered.length === 0 ? (
            <li className="ss__no-results">No results for "{query}"</li>
          ) : (
            filtered.map((item, i) => (
              <li
                key={`${item.id}-${item.label}`}
                data-idx={i}
                className={`ss__option${i === highlightIdx ? " ss__option--active" : ""}`}
                role="option"
                aria-selected={i === highlightIdx}
                onPointerDown={(e) => {
                  e.preventDefault();
                  handleSelect(item);
                }}
                onPointerEnter={() => setHighlightIdx(i)}
              >
                <span className="ss__option-label">{item.label}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      className="ss__icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="5.5"
        cy="5.5"
        r="3.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M8.5 8.5l3 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
